import {
  type CSSProperties,
  type KeyboardEvent,
  type ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

const MARKETPLACE_PREVIEW_CROSSFADE_MS = 220;
const MARKETPLACE_PREVIEW_MOBILE_END_HOLD_MS = 420;

export interface MarketplacePreviewArtifactView {
  id: string;
  role: 'card_cover' | 'detail_gallery';
  position: number;
  routeKey: string;
  variant: 'desktop' | 'mobile';
  source: 'curated_upload' | 'generated_capture' | 'approved_illustration';
  status:
    | 'staged'
    | 'quarantined'
    | 'approved'
    | 'active'
    | 'rejected'
    | 'superseded'
    | 'failed';
  mediaKind: 'still' | 'motion';
  outputProfileKey: string;
  mimeType: string;
  codec: string | null;
  container: string | null;
  locale: string;
  theme: 'light' | 'dark';
  fit: 'cover' | 'contain';
  width: number | null;
  height: number | null;
  focalX: number | null;
  focalY: number | null;
  posterArtifactId: string | null;
}

export interface MarketplacePreviewSetViewModel {
  state: 'ready' | 'pending' | 'failed' | 'unavailable';
  error: string | null;
  artifacts: MarketplacePreviewArtifactView[];
}

export function marketplacePreviewSetViewModel(
  value: unknown
): MarketplacePreviewSetViewModel {
  const parsed = parseMarketplacePreviewSet(value);
  if (parsed)
    return {
      state: 'ready',
      error: hasUnsupportedProfile(value)
        ? 'unsupported_preview_profile'
        : null,
      artifacts: parsed.artifacts,
    };
  if (!isRecord(value)) {
    return { state: 'unavailable', error: null, artifacts: [] };
  }
  if (Array.isArray(value['artifacts'])) {
    return {
      state: 'failed',
      error: hasUnsupportedProfile(value)
        ? 'unsupported_preview_profile'
        : 'invalid_preview_contract',
      artifacts: [],
    };
  }
  const state = value['state'];
  if (state === 'pending') return { state, error: null, artifacts: [] };
  if (state === 'failed') {
    return {
      state,
      error: typeof value['error'] === 'string' ? value['error'] : null,
      artifacts: [],
    };
  }
  return { state: 'unavailable', error: null, artifacts: [] };
}

export function parseMarketplacePreviewSet(value: unknown): {
  artifacts: MarketplacePreviewArtifactView[];
} | null {
  if (!isRecord(value) || !Array.isArray(value['artifacts'])) return null;
  const artifacts = value['artifacts']
    .filter(candidate => !isUnsupportedProfile(candidate))
    .map(parseArtifact);
  if (
    !artifacts.every(
      (item): item is MarketplacePreviewArtifactView => item !== null
    )
  ) {
    return null;
  }
  return artifacts.length ? { artifacts } : null;
}

export function selectMarketplacePreview(
  value: unknown,
  options: {
    role: MarketplacePreviewArtifactView['role'];
    variant?: MarketplacePreviewArtifactView['variant'];
    locale?: string;
    theme?: MarketplacePreviewArtifactView['theme'];
    routeKey?: string;
  }
): MarketplacePreviewArtifactView | null {
  const set = parseMarketplacePreviewSet(value);
  if (!set) return null;
  const roleCandidates = set.artifacts.filter(
    item => item.role === options.role && item.status === 'active'
  );
  const exactRouteCandidates = options.routeKey
    ? roleCandidates.filter(item => item.routeKey === options.routeKey)
    : roleCandidates;
  const candidates = (
    exactRouteCandidates.length
      ? exactRouteCandidates
      : roleCandidates.filter(item => !item.routeKey)
  ).sort(
    (left, right) =>
      sourcePriority(left.source) - sourcePriority(right.source) ||
      mediaPriority(left.mediaKind) - mediaPriority(right.mediaKind) ||
      left.position - right.position
  );
  return (
    candidates.find(
      item =>
        item.variant === (options.variant ?? 'desktop') &&
        (!options.locale || item.locale === options.locale) &&
        (!options.theme || item.theme === options.theme)
    ) ??
    candidates.find(item => item.variant === (options.variant ?? 'desktop')) ??
    candidates[0] ??
    null
  );
}

export function marketplacePreviewUrl(artifactId: string): string {
  return `/api/marketplace/previews/${encodeURIComponent(artifactId)}`;
}

export function MarketplacePreviewMedia({
  artifact,
  alt,
  className,
  allowMotion = false,
  priority = false,
  fallbackLabel = 'Vorschau nicht verfügbar',
}: {
  artifact: MarketplacePreviewArtifactView;
  alt: string;
  className?: string;
  allowMotion?: boolean;
  priority?: boolean;
  fallbackLabel?: string;
}): ReactElement {
  const [failed, setFailed] = useState(false);
  const [motionLoaded, setMotionLoaded] = useState(false);
  const [motionVisible, setMotionVisible] = useState(false);
  const [motionPhase, setMotionPhase] = useState<
    'poster' | 'playing' | 'held' | 'fading'
  >('poster');
  const videoRef = useRef<HTMLVideoElement>(null);
  const motionIntentRef = useRef(false);
  const mobilePlaybackRef = useRef(false);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reducedMotion = useReducedMotion();
  const cancelFade = useCallback(() => {
    if (fadeTimerRef.current !== null) {
      clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }
  }, []);
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') return;
      cancelFade();
      motionIntentRef.current = false;
      setMotionVisible(false);
      setMotionPhase('poster');
      const video = videoRef.current;
      video?.pause();
      if (video) video.currentTime = 0;
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      cancelFade();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [cancelFade]);
  const style: CSSProperties = {
    width: '100%',
    height: '100%',
    display: 'block',
    objectFit: artifact.fit,
    objectPosition: `${Math.round((artifact.focalX ?? 0.5) * 100)}% ${Math.round((artifact.focalY ?? 0.5) * 100)}%`,
  };
  if (failed) {
    return (
      <div
        className={className}
        role="img"
        aria-label={alt || fallbackLabel}
        data-sl-preview-fallback="broken"
        style={{ ...style, display: 'grid', placeItems: 'center' }}
      >
        <span>{fallbackLabel}</span>
      </div>
    );
  }
  if (artifact.mediaKind === 'motion' && allowMotion && !reducedMotion) {
    const poster = artifact.posterArtifactId
      ? marketplacePreviewUrl(artifact.posterArtifactId)
      : undefined;
    const play = (restart = false, mobile = false) => {
      cancelFade();
      mobilePlaybackRef.current = mobile;
      motionIntentRef.current = true;
      setMotionLoaded(true);
      setMotionVisible(true);
      setMotionPhase('playing');
      requestAnimationFrame(() => {
        const video = videoRef.current;
        if (!video || !motionIntentRef.current) return;
        if (restart || video.ended) video.currentTime = 0;
        void video.play().catch(() => {});
      });
    };
    const fadeToPoster = () => {
      cancelFade();
      motionIntentRef.current = false;
      const video = videoRef.current;
      video?.pause();
      if (!video || !motionVisible) {
        setMotionVisible(false);
        setMotionPhase('poster');
        return;
      }
      setMotionPhase('fading');
      setMotionVisible(false);
      fadeTimerRef.current = setTimeout(() => {
        fadeTimerRef.current = null;
        const current = videoRef.current;
        if (current && !motionIntentRef.current) current.currentTime = 0;
        setMotionPhase('poster');
      }, MARKETPLACE_PREVIEW_CROSSFADE_MS);
    };
    return (
      <span
        className={className}
        data-sl-preview-motion={motionPhase}
        tabIndex={0}
        onPointerEnter={event => {
          if (event.pointerType !== 'touch') play();
        }}
        onPointerLeave={event => {
          if (event.pointerType !== 'touch') fadeToPoster();
        }}
        onPointerDown={event => {
          if (event.pointerType !== 'touch') return;
          event.preventDefault();
          play(true, true);
        }}
        onFocus={() => play()}
        onBlur={fadeToPoster}
        style={{ ...style, position: 'relative', overflow: 'hidden' }}
      >
        <img
          src={poster}
          alt={alt}
          draggable={false}
          width={artifact.width ?? undefined}
          height={artifact.height ?? undefined}
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : 'auto'}
          decoding="async"
          style={{
            ...style,
            position: 'absolute',
            inset: 0,
            opacity: motionVisible ? 0 : 1,
            transition: `opacity ${MARKETPLACE_PREVIEW_CROSSFADE_MS}ms ease`,
          }}
        />
        <video
          ref={videoRef}
          src={motionLoaded ? marketplacePreviewUrl(artifact.id) : undefined}
          poster={poster}
          aria-hidden="true"
          muted
          playsInline
          preload={motionLoaded ? 'auto' : 'none'}
          onCanPlay={() => {
            if (motionIntentRef.current) {
              void videoRef.current?.play().catch(() => {});
            }
          }}
          onEnded={() => {
            motionIntentRef.current = false;
            if (mobilePlaybackRef.current) {
              setMotionVisible(true);
              setMotionPhase('held');
              cancelFade();
              fadeTimerRef.current = setTimeout(() => {
                fadeTimerRef.current = null;
                fadeToPoster();
              }, MARKETPLACE_PREVIEW_MOBILE_END_HOLD_MS);
            } else {
              setMotionVisible(true);
              setMotionPhase('held');
            }
          }}
          onError={() => setFailed(true)}
          style={{
            ...style,
            position: 'absolute',
            inset: 0,
            opacity: motionVisible ? 1 : 0,
            transition: `opacity ${MARKETPLACE_PREVIEW_CROSSFADE_MS}ms ease`,
          }}
        />
      </span>
    );
  }
  const imageId =
    artifact.mediaKind === 'motion' && artifact.posterArtifactId
      ? artifact.posterArtifactId
      : artifact.id;
  return (
    <img
      className={className}
      src={marketplacePreviewUrl(imageId)}
      alt={alt}
      width={artifact.width ?? undefined}
      height={artifact.height ?? undefined}
      loading={priority ? 'eager' : 'lazy'}
      fetchPriority={priority ? 'high' : 'auto'}
      decoding="async"
      onError={() => setFailed(true)}
      style={style}
    />
  );
}

export function MarketplacePreviewGallery({
  previewSet,
  label,
}: {
  previewSet: unknown;
  label: string;
}): ReactElement | null {
  const listRef = useRef<HTMLUListElement>(null);
  const mobile = useMobilePreview();
  const set = parseMarketplacePreviewSet(previewSet);
  const artifacts = set?.artifacts
    .filter(
      item =>
        item.role === 'detail_gallery' &&
        item.status === 'active' &&
        item.variant === (mobile ? 'mobile' : 'desktop')
    )
    .sort(
      (left, right) =>
        left.position - right.position ||
        mediaPriority(left.mediaKind) - mediaPriority(right.mediaKind)
    )
    .filter(
      (item, index, all) =>
        all.findIndex(
          candidate =>
            candidate.outputProfileKey === item.outputProfileKey &&
            candidate.variant === item.variant &&
            candidate.position === item.position
        ) === index
    );
  if (!artifacts?.length) return null;
  const move = (direction: -1 | 1) => {
    const list = listRef.current;
    if (!list) return;
    list.scrollBy({
      left: direction * list.clientWidth * 0.8,
      behavior: 'smooth',
    });
  };
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    move(event.key === 'ArrowLeft' ? -1 : 1);
  };
  return (
    <section
      aria-label={label}
      onKeyDown={onKeyDown}
      style={{ padding: '8px 0 28px' }}
    >
      <div aria-label={`${label} Navigation`} role="group">
        <button type="button" onClick={() => move(-1)} aria-label="Zurück">
          ←
        </button>
        <button type="button" onClick={() => move(1)} aria-label="Weiter">
          →
        </button>
      </div>
      <ul
        ref={listRef}
        tabIndex={0}
        style={{
          display: 'grid',
          gridAutoFlow: 'column',
          gridAutoColumns: 'min(86vw, 1040px)',
          gap: 16,
          overflowX: 'auto',
          overscrollBehaviorInline: 'contain',
          scrollSnapType: 'x mandatory',
          listStyle: 'none',
          margin: 0,
          padding: '0 max(24px, 4vw) 12px',
        }}
      >
        {artifacts.map((artifact, index) => (
          <li
            key={artifact.id}
            style={{
              aspectRatio:
                artifact.width && artifact.height
                  ? `${artifact.width}/${artifact.height}`
                  : '16/10',
              maxHeight: '68vh',
              overflow: 'hidden',
              borderRadius: 20,
              border: '1px solid var(--affine-border-color, #deddd9)',
              background: 'var(--affine-background-secondary-color, #f4f4f1)',
              scrollSnapAlign: 'center',
            }}
          >
            <MarketplacePreviewMedia
              artifact={artifact}
              alt={`${label} ${index + 1}`}
              allowMotion
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

function parseArtifact(value: unknown): MarketplacePreviewArtifactView | null {
  if (!isRecord(value)) return null;
  const role = value['role'];
  const variant = value['variant'];
  const mediaKind = value['mediaKind'];
  const theme = value['theme'];
  const fit = value['fit'];
  const source = value['source'];
  const status = value['status'];
  if (
    typeof value['id'] !== 'string' ||
    (role !== 'card_cover' && role !== 'detail_gallery') ||
    (variant !== 'desktop' && variant !== 'mobile') ||
    (mediaKind !== 'still' && mediaKind !== 'motion') ||
    (theme !== 'light' && theme !== 'dark') ||
    (fit !== 'cover' && fit !== 'contain') ||
    !['curated_upload', 'generated_capture', 'approved_illustration'].includes(
      String(source)
    ) ||
    ![
      'staged',
      'quarantined',
      'approved',
      'active',
      'rejected',
      'superseded',
      'failed',
    ].includes(String(status)) ||
    !isKnownProfile(value['outputProfileKey'], role, variant) ||
    typeof value['mimeType'] !== 'string' ||
    typeof value['locale'] !== 'string' ||
    (mediaKind === 'motion' &&
      (typeof value['posterArtifactId'] !== 'string' ||
        !value['posterArtifactId']))
  ) {
    return null;
  }
  const codec = typeof value['codec'] === 'string' ? value['codec'] : null;
  const container =
    typeof value['container'] === 'string' ? value['container'] : null;
  if (!isCompatibleMedia(mediaKind, value['mimeType'], codec, container)) {
    return null;
  }
  return {
    id: value['id'],
    role,
    position: finiteNumber(value['position'], 0),
    routeKey: typeof value['routeKey'] === 'string' ? value['routeKey'] : '',
    variant,
    source: source as MarketplacePreviewArtifactView['source'],
    status: status as MarketplacePreviewArtifactView['status'],
    mediaKind,
    outputProfileKey: value['outputProfileKey'],
    mimeType: value['mimeType'],
    codec,
    container,
    locale: value['locale'],
    theme,
    fit,
    width: nullableNumber(value['width']),
    height: nullableNumber(value['height']),
    focalX: nullableNumber(value['focalX']),
    focalY: nullableNumber(value['focalY']),
    posterArtifactId:
      typeof value['posterArtifactId'] === 'string'
        ? value['posterArtifactId']
        : null,
  };
}

function isKnownProfile(
  value: unknown,
  role: unknown,
  variant: unknown
): value is string {
  return (
    (value === 'desktop_card' &&
      role === 'card_cover' &&
      variant === 'desktop') ||
    (value === 'desktop_detail' &&
      role === 'detail_gallery' &&
      variant === 'desktop') ||
    (value === 'mobile_detail' &&
      role === 'detail_gallery' &&
      variant === 'mobile')
  );
}

function hasUnsupportedProfile(value: unknown): boolean {
  return (
    isRecord(value) &&
    Array.isArray(value['artifacts']) &&
    value['artifacts'].some(isUnsupportedProfile)
  );
}

function isUnsupportedProfile(value: unknown): boolean {
  if (!isRecord(value) || typeof value['outputProfileKey'] !== 'string') {
    return false;
  }
  return !['desktop_card', 'desktop_detail', 'mobile_detail'].includes(
    value['outputProfileKey']
  );
}

function isCompatibleMedia(
  mediaKind: unknown,
  mimeType: string,
  codec: string | null,
  container: string | null
): boolean {
  if (mediaKind === 'still') {
    return (
      codec === null &&
      container === null &&
      ['image/avif', 'image/jpeg', 'image/png', 'image/webp'].includes(mimeType)
    );
  }
  return (
    (mimeType === 'video/webm' && codec === 'vp9' && container === 'webm') ||
    (mimeType === 'video/mp4' && codec === 'h264' && container === 'mp4')
  );
}

function sourcePriority(source: MarketplacePreviewArtifactView['source']) {
  return {
    curated_upload: 0,
    generated_capture: 1,
    approved_illustration: 2,
  }[source];
}

function mediaPriority(mediaKind: MarketplacePreviewArtifactView['mediaKind']) {
  return mediaKind === 'motion' ? 0 : 1;
}

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!query) return;
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);
  return reduced;
}

function useMobilePreview(): boolean {
  const [mobile, setMobile] = useState(false);
  useEffect(() => {
    const query = window.matchMedia?.('(max-width: 600px)');
    if (!query) return;
    const update = () => setMobile(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);
  return mobile;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function finiteNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}
function nullableNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}
